// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Click-to-explain popovers for domain jargon. Any element carrying
// `data-term="<key>"` becomes a trigger; one delegated listener serves the
// whole document, so views can emit `glossTerm(...)` into an innerHTML string
// without wiring anything up.

import { GLOSSARY } from '../glossary';
import { closeButton, openOverlay } from '../overlay';
import { esc } from '../format';

/** Markup for an inline glossary trigger, for use inside an innerHTML string. */
export function glossTerm(key: string, label?: string): string {
  const t = GLOSSARY[key];
  if (!t) return esc(label ?? key);
  return `<button type="button" class="glossary-link" data-term="${esc(key)}" aria-label="What does ${esc(t.term)} mean?">${esc(label ?? t.term)}</button>`;
}

/** A standalone ⓘ trigger, for putting next to a heading or a stat. */
export function glossIcon(key: string): string {
  const t = GLOSSARY[key];
  if (!t) return '';
  return `<button type="button" class="glossary-icon" data-term="${esc(key)}" aria-label="What does ${esc(t.term)} mean?">i</button>`;
}

export function openGlossary(key: string): void {
  const t = GLOSSARY[key];
  if (!t) return;
  openOverlay({
    panelClass: 'gloss-pop',
    label: `Definition: ${t.term}`,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.textContent = t.term;
      head.append(h, closeButton(close, `Close the definition of ${t.term}`));
      const body = document.createElement('div');
      body.className = 'stack small';
      body.innerHTML = `<p>${esc(t.short)}</p>${t.detail ? `<p class="muted">${esc(t.detail)}</p>` : ''}`;
      panel.append(head, body);
    },
  });
}

export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const trigger = (e.target as Element)?.closest?.('[data-term]');
    if (!trigger) return;
    e.preventDefault();
    openGlossary(trigger.getAttribute('data-term') ?? '');
  });
}
